import { IGlobalStore } from 'src/types/store'
import {
  TStateCommunications,
  createStateSagaCommunications,
} from 'src/helpers/redux'

import { IModuleConfig } from './index'
import { Actions } from './actions'
import * as smartListTypes from 'src/services/storage/project/scheme/smart-list/types'
import * as schemaService from 'src/services/storage/project/scheme/types'
import { getSession } from 'src/services/session'
import { IObjectBoardConfig } from 'src/helpers/storageObjects'
import {
  IPostImportDataOptions,
  EImportDataStrategy,
} from 'src/services/storage/project/scheme/import/types'

export * from 'src/services/storage/project/scheme/types'

export type FieldValue = string | number | null | undefined

export interface IListItem extends schemaService.IGetObjectsData {
  commentsCount?: number
}

export interface IUpdateObjectItem {
  data: IListItem
}

export interface IColumn {
  identifier: string
  objectValueKey: string
  slug: string
  enable: boolean
  width?: number
  priority: {
    index: number
    static: boolean
  }
  schema?: schemaService.ISchema
}

export interface IStateData {
  smartList: {
    itemDefault?: smartListTypes.IGetSmartListItemData
    itemDefaultUserOptions?: smartListTypes.IPatchSmartListDefaultUserOptionsData
    item?: smartListTypes.IGetSmartListItemData
    permissions: smartListTypes.IGetSmartListPermissionsData

    list: {
      items: smartListTypes.IGetSmartListData
      nextCursor?: string | null
    }
  }

  objects: {
    item?: IListItem
    deleteValidation: schemaService.IDeleteValidationObjectByIdData[] | null
    list: {
      parentObjectUniqId?: string
      items: IListItem[]
      previousCursor: string | null
      nextCursor: string | null
      total?: number
    }
  }

  fieldsGroups: {
    list: {
      items: schemaService.IFieldsGroupData[]
      previousCursor: string | null
      nextCursor: string | null
    }
  }
  table: {
    columns: IColumn[]
    filters: { [key: string]: unknown }
  }
  board: {
    config: IObjectBoardConfig
    filters: { [key: string]: unknown }
  }
  import?: {
    strategy?: `${EImportDataStrategy}`
    options?: IPostImportDataOptions
    mapping?: {
      columnName: string
      fieldIdentifier: string
      primary: boolean
    }[]
  }
}

export interface IState {
  data: IStateData
  communication: TStateCommunications<Actions>
}
const getStorageState = (key: string) => {
  try {
    return JSON.parse(localStorage.getItem(key) || '{}')
  } catch (error) {
    return {}
  }
}
export const getInitialData = (config: IModuleConfig): IStateData => {
  const stateData = getStorageState(config.prefix)

  const session = getSession()
  const isCurrent = (item: IModuleConfig) =>
    item._prefix === config._prefix && item.schemeName === config.schemeName
  const storageData = (session.storage || []).find(isCurrent)

  return {
    smartList: {
      item: undefined,
      permissions: [],
      itemDefault: storageData?.smartListItemDefault || undefined,
      list: {
        items: storageData?.smartListsItems || [],
        nextCursor: undefined,
      },
    },

    objects: {
      item: undefined,
      deleteValidation: null,
      list: {
        items: storageData?.objectItems || [],
        previousCursor: null,
        nextCursor: null,
      },
    },

    fieldsGroups: {
      list: {
        items: storageData?.fieldsGroupsItems || [],
        previousCursor: null,
        nextCursor: null,
      },
    },

    table: storageData?.table || {
      columns: [],
      filters: {},
    },
    board: storageData?.board || {
      config: [],
      filters: {},
    },

    import: storageData?.import,

    ...stateData,
  }
}

export const getInitialState = (
  config: IModuleConfig,
  actions: Actions
): IState => ({
  data: getInitialData(config),
  communication: createStateSagaCommunications(actions),
})

export type TGlobalStateWithService = IGlobalStore & {
  [key: string]: IState
}
